import { useState, useRef } from 'react'
import * as tus from 'tus-js-client'
import { X, Upload, Film, Loader2 } from 'lucide-react'
import { useVideoStore } from '../../stores/videoStore'
import { videosApi } from '../../services/api'

interface VideoUploadProps {
  onClose: () => void
  onSuccess?: () => void
}

export default function VideoUpload({ onClose, onSuccess }: VideoUploadProps) {
  const { createVideo } = useVideoStore()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [step, setStep] = useState<'details' | 'uploading' | 'done'>('details')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadRef = useRef<tus.Upload | null>(null)

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      // Validate file type
      if (!selectedFile.type.startsWith('video/')) {
        setError('Please select a video file')
        return
      }
      // Validate file size (max 1GB)
      if (selectedFile.size > 1024 * 1024 * 1024) {
        setError('File size must be less than 1GB')
        return
      }
      setFile(selectedFile)
      setError(null)
      // Auto-fill title from filename if empty
      if (!title) {
        setTitle(selectedFile.name.replace(/\.[^/.]+$/, ''))
      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) {
      setError('Title is required')
      return
    }
    if (!file) {
      setError('Please select a video file')
      return
    }

    setIsUploading(true)
    setError(null)
    setStep('uploading')

    try {
      // Step 1: Create video record
      const video = await createVideo({ title: title.trim(), description: description.trim() })

      // Step 2: Get upload URL from Cloudflare (with file info for TUS)
      const uploadUrl = await videosApi.getUploadUrl(video.id, file.size, file.name)

      if (!uploadUrl) {
        throw new Error('Failed to get upload URL')
      }

      // Step 3: Upload file to Cloudflare
      await uploadWithTus(uploadUrl, file)

      // Step 4: Notify backend that upload is complete
      await videosApi.markUploadComplete(video.id)

      setStep('done')
      setTimeout(() => {
        onSuccess?.()
        onClose()
      }, 1500)
    } catch (err) {
      console.error('Upload error:', err)
      setError(err instanceof Error ? err.message : 'Failed to upload video')
      setStep('details')
    } finally {
      setIsUploading(false)
    }
  }

  const uploadWithTus = async (uploadUrl: string, file: File): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      const upload = new tus.Upload(file, {
        uploadUrl: uploadUrl,
        chunkSize: 5 * 1024 * 1024, // 5 MiB — divisible by 256 KiB
        retryDelays: [0, 1000, 3000, 5000],
        metadata: {
          filename: file.name,
          filetype: file.type,
        },
        onProgress: (bytesUploaded, bytesTotal) => {
          const progress = Math.round((bytesUploaded / bytesTotal) * 100)
          setUploadProgress(progress)
        },
        onSuccess: () => {
          uploadRef.current = null
          resolve()
        },
        onError: (err) => {
          uploadRef.current = null
          reject(new Error(err.message || 'Upload failed. Please check your connection and try again.'))
        },
      })

      uploadRef.current = upload
      upload.start()
    })
  }

  const handleClose = () => {
    if (uploadRef.current) {
      uploadRef.current.abort()
      uploadRef.current = null
    }
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-slate-800 rounded-xl shadow-2xl w-full max-w-lg mx-4 border border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white">Upload Video</h2>
          <button
            onClick={handleClose}
            className="p-1 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {step === 'details' && (
            <>
              {/* File selector */}
              <div
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  file
                    ? 'border-primary-500 bg-primary-500/10'
                    : 'border-slate-600 hover:border-slate-500'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                {file ? (
                  <div className="space-y-2">
                    <Film className="w-12 h-12 mx-auto text-primary-500" />
                    <p className="text-white font-medium">{file.name}</p>
                    <p className="text-sm text-slate-400">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-12 h-12 mx-auto text-slate-400" />
                    <p className="text-white">Click to select a video</p>
                    <p className="text-sm text-slate-400">MP4, MOV, WebM up to 1GB</p>
                  </div>
                )}
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter video title"
                  className="input w-full"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional description"
                  rows={3}
                  className="input w-full resize-none"
                />
              </div>

              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}
            </>
          )}

          {step === 'uploading' && (
            <div className="py-8 text-center space-y-4">
              <Loader2 className="w-12 h-12 mx-auto text-primary-500 animate-spin" />
              <div>
                <p className="text-white font-medium">Uploading video...</p>
                <p className="text-sm text-slate-400">{uploadProgress}% complete</p>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-2">
                <div
                  className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}
            </div>
          )}

          {step === 'done' && (
            <div className="py-8 text-center space-y-4">
              <div className="w-12 h-12 mx-auto bg-green-500/20 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-white font-medium">Video uploaded successfully!</p>
            </div>
          )}

          {/* Footer */}
          {step === 'details' && (
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="btn-ghost"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!file || !title.trim() || isUploading}
                className="btn-primary disabled:opacity-50"
              >
                Upload Video
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}
